/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @author Petr Mikušek <petr.mikusek@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

/* eslint-env browser */

import AssertionError from 'assertion-error';
import { errorWithName } from '../error/error-with-name';
import { fetchWithTimeout } from '../fetch/fetch';

/**
 * @import { IAnalyticsTransporter, ReadonlyGroupCounters } from '.'
 * @import { ErrorWithStackDeterministic } from '../../util.unit'
 * @import { AnalyticsCounterAttrs } from './analytics-counter-attrs'
 * @import { AnalyticsGroupAttrs } from './analytics-group-attrs'
 * @import { DeviceAnalyticsRequestMedium, DeviceAnalyticsRequest } from './analytics-server'
 */

/**
 * Transportér analytických dat.
 * Odesílá analytická data na Ki-Wi Server.
 * @implements {IAnalyticsTransporter}
 */
export default class AnalyticsTransporter {
  /** @type {IAnalyticsTransporter['transport']} */
  async transport(group, counters, { timeoutInMs = undefined } = {}) {
    // Toto je logicky AssertionError, ale z hlediska robustnosti musí být
    // PermanentError, jelikož při každém opakovaném volání vždy vrhneme.
    if (counters.length === 0) {
      const cause = new AssertionError('AnalyticsTransporter: empty counters');
      throw errorWithName('PermanentError', cause.toString(), { cause });
    }

    const { serverUrl, deviceId } = group;
    const url = `${serverUrl}/api/device/${deviceId}/analytics`;
    const request = generateDeviceAnalyticsRequest(group, counters);
    const body = JSON.stringify(request);
    const response = await fetchWithTimeout(
      url,
      {
        body,
        headers: { 'Content-type': 'application/json' },
        method: 'POST'
      },
      timeoutInMs
    );
    if (response.ok) {
      return;
    }
    const { status } = response;
    const message = `HTTP status ${response.status}`;
    // TODO #389: Ki-Wi Server může vrátit jen stavy 200, 400, 404 a 500,
    // ale pro robustnost jsou permanentní všechny 4xx kromě 408 Request Timeout.
    // - https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/408
    if (status >= 400 && status <= 499 && status !== 408) {
      throw errorWithName('PermanentError', message);
    }
    throw new Error(message);
  }
}

/**
 * Vygeneruje analytickou skupinu ve formátu pro server.
 * @type {(...args: ReadonlyGroupCounters) => DeviceAnalyticsRequest}
 */
export function generateDeviceAnalyticsRequest(
  { deviceId, opened: start, closed: end },
  counters
) {
  // Posílání `name` místo `filename` je záměrné.
  const media = counters.map(
    ({ played: count, name: filename, mediumId: id, type: mimetype }) =>
      /** @satisfies {DeviceAnalyticsRequestMedium} */
      ({ count, filename, id, mimetype })
  );
  return { deviceId, start, end, media };
}

// TODO #389: Problém zbytečného percent a UTF-8 ecodingu kvůli ` (19).png`, obecně
// file name, co mají na začátku (a možná na konci) whitespace a případně
// obsahují závorky a dvojtečky. Možná by šlo řešit tím, že by se leading/trailing
// mezery explicitně kódovaly pomocí `%20` (ale mezery uprostřed nikoliv) a navíc
// problémové znaky, které nepřipouští stack trace (ne)specifikace by se percent
// kódovaly taky. Zpětný převod by znamenal použití decodeURI{,Component} vždy.
// Vlastně ale pak se i % musí kódovat, protože jinak by dekódování mohlo
// nechtěně interpretovat.
/**
 * Zmodifikuje {@link Error.stack} trace in-place na zakódovanou analytickou
 * skupinu včetně čítačů a vrátí zmodifikovaný {@link error}.
 * @template {Error} T
 * @param {T} error
 * @param {Readonly<AnalyticsGroupAttrs>} group
 * @param {readonly Readonly<AnalyticsCounterAttrs>[] | undefined} counters
 * @returns {ErrorWithStackDeterministic<T>}
 */
export function errorWithAnalyticsStackTrace(
  error,
  { opened, closed },
  counters
) {
  // Zachování prvního řádku pomocí toString(), tj. name: message.
  error.stack = `${error}`;
  // TODO #389: Nedořešené, obsahuje dvojtečky, což je nepřípustné, ale např.
  // Sentry to nevadí.
  error.stack += `\n    at ${opened}/${closed}`;
  if (counters) {
    counters.forEach(({ mediumId, name, played }) => {
      error.stack += `\n    at ${encodeStackFilename(
        name
      )}:${mediumId}:${played}`;
    });
  }
  return Object.defineProperty(error, 'stackDeterministic', {
    enumerable: false,
    writable: false,
    value: true
  });
}

/**
 * Rozšířená verze encodeURI (vychází z RFC3986), která oproti navíc kóduje i ':'
 * (kvůli stack trace, kde dvojtečka je oddělovač řádku a sloupce).
 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/encodeURI#encoding_for_rfc3986
 * @param {string} str
 * @returns {string}
 */
export function encodeStackFilename(str) {
  return encodeURI(str)
    .replace(/%5B/g, '[')
    .replace(/%5D/g, ']')
    .replace(
      /[!'()*:]/g,
      (c) => `%${c.charCodeAt(0).toString(16).toUpperCase()}`
    );
}
